import React, { useRef } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useAuth } from "@/contexts/auth/useAuth";
import { cn } from "@/utils/cn";

import AltPopover from "./attachments/alt-popover";
import { Badge } from "./ui";

export interface StillImageProps {
  /** Image alt text. */
  alt?: string;
  /** Extra class names for the outer <div> container. */
  className?: string;
  /** URL to the image */
  src: string;
  /** Extra CSS styles on the outer <div> element. */
  style?: React.CSSProperties;
  /** Whether to display the image contained vs filled in its container. */
  letterboxed?: boolean;
  /** Whether to show the file extension in the corner. */
  showExt?: boolean;
  /** Callback function if the image fails to load */
  onError?(): void;
  onLoad?(): void;
  width?: number;
  height?: number;
}

const messages = defineMessages({
  alt: { id: "image.alt", defaultMessage: "ALT" },
  altTitle: {
    id: "image.alt_dialog.title",
    defaultMessage: "Image Description",
  },
  altButton: { id: "image.alt_dialog.button", defaultMessage: "Dismiss" },
  gif: { id: "image.gif", defaultMessage: "GIF" },
});

/** Renders images on a canvas, only playing GIFs if autoPlayGif is enabled. */
const StillImage: React.FC<StillImageProps> = ({
  alt,
  className,
  src,
  style,
  letterboxed = false,
  showExt = false,
  onError,
  onLoad,
  width,
  height,
}) => {
  const intl = useIntl();
  const { currentAccount } = useAuth();
  const autoPlayGif = currentAccount?.settings?.autoPlayGif;

  const canvas = useRef<HTMLCanvasElement>(null);
  const img = useRef<HTMLImageElement>(null);

  const hoverToPlay =
    src && !autoPlayGif && (src.endsWith(".gif") || src.startsWith("blob:"));

  const handleImageLoad = () => {
    if (onLoad) onLoad();

    if (hoverToPlay && canvas.current && img.current) {
      canvas.current.width = img.current.naturalWidth;
      canvas.current.height = img.current.naturalHeight;
      canvas.current.getContext("2d")?.drawImage(img.current, 0, 0);
    }
  };

  /** ClassNames shared between the `<img>` and `<canvas>` elements. */
  const baseClassName = cn("block size-full", {
    "object-contain": letterboxed,
    "object-cover": !letterboxed,
  });

  return (
    <div
      data-testid="still-image-container"
      className={cn("group relative isolate overflow-hidden", className)}
      style={style}
    >
      <img
        src={src}
        alt={alt}
        ref={img}
        onLoad={handleImageLoad}
        onError={onError}
        className={cn(baseClassName, {
          "invisible group-hover:visible": hoverToPlay,
        })}
        width={width}
        height={height}
      />

      {hoverToPlay && (
        <canvas
          ref={canvas}
          className={cn(baseClassName, {
            "group-hover:invisible": hoverToPlay,
          })}
        />
      )}

      <div className="absolute bottom-2 left-2 z-1 flex items-center space-x-2">
        {hoverToPlay && showExt && (
          <div className="pointer-events-none group-hover:hidden">
            <Badge variant="overlay" className="cursor-pointer">
              {intl.formatMessage(messages.gif)}
            </Badge>
          </div>
        )}

        {showExt && alt && <AltPopover description={alt} />}
      </div>
    </div>
  );
};

export default StillImage;
